import { useSyncExternalStore } from 'react';

export interface Toast {
  id: number;
  message: string;
  tone?: 'done' | 'missed' | 'skipped' | 'neutral';
  actionLabel?: string;
  onAction?: () => void;
  /** Called when the toast disappears without the action being used. */
  onExpire?: () => void;
}

let current: Toast | null = null;
let timer: number | undefined;
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => {
  listeners.forEach((fn) => {
    fn();
  });
};

function settle(useAction: boolean) {
  const t = current;
  if (!t) return;
  window.clearTimeout(timer);
  current = null;
  emit();
  if (useAction) t.onAction?.();
  else t.onExpire?.();
}

/** Shows a toast; a previous toast is expired (its pending action is committed) first. */
export function showToast(toast: Omit<Toast, 'id'>, durationMs = 5000): void {
  settle(false);
  current = { ...toast, id: nextId++ };
  emit();
  timer = window.setTimeout(() => {
    settle(false);
  }, durationMs);
}

export const toastAction = () => {
  settle(true);
};
export const dismissToast = () => {
  settle(false);
};

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
export const useToast = () => useSyncExternalStore(subscribe, () => current);
